"use client";

import {
  type FocusEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  type AiModelAccess,
  isRestrictedHighTierModel,
} from "@/lib/ai/model-access";
import { isValidModelId, type ModelId } from "@/lib/ai/models";

type UseChatPromptExpansionParams = {
  hasDraftInput: boolean;
  mentionMenuOpen: boolean;
  aiModelAccess: AiModelAccess;
  onModelChange: (model: ModelId) => void;
  compactWhenInactive?: boolean;
  keepExpanded?: boolean;
};

/**
 * compact / expanded 切り替えに必要な最小 UI 状態だけを管理する。
 *
 * 設計方針:
 * - UI の生 state は `hasFocusWithin` / overlay open flags / `pendingBlurOutside`
 *   に限定する
 * - 入力中・メンション中・streaming 中といった blocker は導出値に寄せる
 * - blur 時に blocker が残っていても `pendingBlurOutside` を保持し、
 *   blocker 解消後に 1 箇所で compact 復帰を再評価する
 */
export function useChatPromptExpansion({
  hasDraftInput,
  mentionMenuOpen,
  aiModelAccess,
  onModelChange,
  compactWhenInactive = true,
  keepExpanded = false,
}: UseChatPromptExpansionParams) {
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const [isContextPickerOpen, setContextPickerOpen] = useState(false);
  const [isModelSelectOpen, setModelSelectOpen] = useState(false);
  const [isUpgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [pendingBlurOutside, setPendingBlurOutside] = useState(false);
  const [supportsContentFieldSizing, setSupportsContentFieldSizing] =
    useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);

  /**
   * Portal 系 UI は trigger から一時的に focus を外すため、
   * 「overlay が何か」ではなく「何かしら開いているか」だけ分かれば十分。
   */
  const hasOpenOverlay =
    isContextPickerOpen || isModelSelectOpen || isUpgradeDialogOpen;
  const hasBlockingActivity = Boolean(
    hasDraftInput || mentionMenuOpen || keepExpanded,
  );
  const canRestoreCompact =
    compactWhenInactive && !hasOpenOverlay && !hasBlockingActivity;
  const isExpanded =
    !compactWhenInactive ||
    hasFocusWithin ||
    hasOpenOverlay ||
    hasBlockingActivity;
  const textareaRows = isExpanded && !supportsContentFieldSizing ? 2 : 1;

  useEffect(() => {
    setSupportsContentFieldSizing(
      typeof CSS !== "undefined" &&
        typeof CSS.supports === "function" &&
        CSS.supports("field-sizing", "content"),
    );
  }, []);

  useEffect(() => {
    if (!pendingBlurOutside || !canRestoreCompact) {
      return;
    }

    const activeElement = document.activeElement;
    if (
      activeElement instanceof Node &&
      containerRef.current?.contains(activeElement)
    ) {
      setPendingBlurOutside(false);
      return;
    }

    setHasFocusWithin(false);
    setPendingBlurOutside(false);
  }, [canRestoreCompact, pendingBlurOutside]);

  const handleModelValueChange = useCallback(
    (value: string) => {
      if (!isValidModelId(value)) {
        return;
      }
      if (isRestrictedHighTierModel(value, aiModelAccess)) {
        setUpgradeDialogOpen(true);
        return;
      }
      onModelChange(value);
    },
    [aiModelAccess, onModelChange],
  );

  const handleFocusCapture = useCallback(() => {
    setHasFocusWithin(true);
    setPendingBlurOutside(false);
  }, []);

  const handleBlurCapture = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const nextFocusedNode = event.relatedTarget;
    if (
      nextFocusedNode instanceof Node &&
      event.currentTarget.contains(nextFocusedNode)
    ) {
      return;
    }

    setPendingBlurOutside(true);
  }, []);

  return {
    containerRef,
    isExpanded,
    textareaRows,
    isUpgradeDialogOpen,
    setContextPickerOpen,
    setModelSelectOpen,
    setUpgradeDialogOpen,
    handleModelValueChange,
    handleFocusCapture,
    handleBlurCapture,
  };
}
